/**
 * @file 输入态引用串文案构建（Skill / Knowledge / Chart）
 * 不含 React 依赖，纯函数层。
 */
import type { ISelectedSkillTagData } from '../SkillMention/SelectedSkillTag';
import {
  resolveSelectedSkillDisplayName,
} from '../SkillMention/SelectedSkillTag';
import type { ISelectedKnowledgeTagData } from '../KnowledgeBaseMention/components/SelectedKnowledgeTag';
import type { ISelectedChartTagData } from '../ChartPopover/components/SelectedChartTag';
import { resolveChartDisplayName } from '../ChartPopover/components/SelectedChartTag';
import { MENTION_BOUNDARY_MARKER } from './inlineMentionRanges';

/** 历史兼容：旧版输入态在触发符后插入的空格（单空格） */
export const MENTION_TRIGGER_GAP = ' ';

/** 历史兼容：旧版知识库 @ 后双空格（mirror 视觉对齐遗留） */
export const MENTION_KNOWLEDGE_TRIGGER_GAP = '  ';

/** 新版提交/存储：触发符后无空格，如 `@库名-文档.md`、`/Skill名` */
export const MENTION_SERIALIZE_GAP = '';

/** `@` / `/` / `#` 后接正文 */
export function withMentionTrigger(
  trigger: '@' | '/' | '#',
  body: string,
  gap: string = MENTION_SERIALIZE_GAP,
): string {
  const trimmed = body.trim();
  if (!trimmed) return '';
  return `${trigger}${gap}${trimmed}`;
}

/** 知识库 @ 引用串（新版无间隔；历史带空格由 mentionLabelMatchVariants 兼容） */
export function withKnowledgeMentionTrigger(body: string): string {
  return withMentionTrigger('@', body, MENTION_SERIALIZE_GAP);
}

/**
 * mention token 展示正文（不含 @ / 及间隔空格；左侧 icon 已表示类型）
 * 仅用于从「提交用完整引用串」创建 DOM 时解析一次
 */
export function getMentionDisplayBody(
  label: string,
  kind: 'knowledge' | 'skill',
): string {
  const trimmed = label.trim();
  const trigger = kind === 'knowledge' ? '@' : '/';
  if (!trimmed.startsWith(trigger)) return trimmed;
  return trimmed.slice(1).replace(/^\s+/, '');
}

/** 由展示正文 + 类型还原提交用引用串（序列化 / 选区偏移计算，无触发符后空格） */
export function buildMentionSerializeLabel(
  body: string,
  kind: 'knowledge' | 'skill',
): string {
  const trimmed = body.trim();
  if (!trimmed) return '';
  return withMentionTrigger(kind === 'knowledge' ? '@' : '/', trimmed, MENTION_SERIALIZE_GAP);
}

/**
 * 同一引用串的新旧两种写法（有/无触发符后空格），用于正文匹配与历史兼容
 */
export function mentionLabelMatchVariants(label: string): string[] {
  const trimmed = label.trim();
  if (!trimmed) return [];

  const variants = new Set<string>([trimmed]);
  const matched = trimmed.match(/^([@/#])(\s*)([\s\S]+)$/);
  if (!matched) return [trimmed];

  const [, trigger, , body] = matched;
  // 新版紧凑格式优先
  variants.add(`${trigger}${body}`);
  // 历史：触发符后 1～2 个空格
  variants.add(`${trigger}${MENTION_TRIGGER_GAP}${body}`);
  if (trigger === '@') {
    variants.add(`${trigger}${MENTION_KNOWLEDGE_TRIGGER_GAP}${body}`);
  }
  return Array.from(variants);
}

/** 确保引用串末尾有空格，方便继续输入 */
export function withTrailingSpace(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return '';
  return `${trimmed} `;
}

/** Skill 光标处插入文案：`/展示名 ` */
export function buildSkillInlineLabel(
  skill: ISelectedSkillTagData,
  isEN: boolean,
): string {
  // 展示名若自带 `/` 前缀，避免拼成 `//Skill` 导致 hydrate 残留孤立 `/` 文本
  const name = resolveSelectedSkillDisplayName(skill, isEN)
    .replace(/^\/+\s*/, '')
    .trim();
  return withTrailingSpace(withMentionTrigger('/', name, MENTION_SERIALIZE_GAP));
}

/**
 * 写入输入框并 hydrate 为 inline tag 的 Skill 串（紧凑格式 + 边界符）
 * 用于外链跳转、历史 queryTabs 合成等声明式预填场景
 */
export function buildSerializedSkillInlineLabel(
  skill: ISelectedSkillTagData,
  isEN: boolean,
): string {
  const name = resolveSelectedSkillDisplayName(skill, isEN)
    .replace(/^\/+\s*/, '')
    .trim();
  if (!name) return '';
  return `/${name}${MENTION_BOUNDARY_MARKER}`;
}

/**
 * 知识库光标处插入文案（按库分组）：
 * - ≤2 个文档：`@库名-文档1、文档2`（合并展示）
 * - ≥3 个文档：`@库名 N篇`（数量展示）
 */
export function buildKnowledgeInlineLabel(
  items: ISelectedKnowledgeTagData[],
  docUnit = '篇',
): string {
  if (!items?.length) return '';

  const byKb = new Map<string, ISelectedKnowledgeTagData[]>();
  items.forEach((item) => {
    const list = byKb.get(item.kbId) || [];
    list.push(item);
    byKb.set(item.kbId, list);
  });

  const parts = Array.from(byKb.values()).map((list) => {
    const kbName = list[0]?.kbName || '';
    if (!kbName) return '';
    const docs = list.filter((item) => item.docName);
    if (docs.length === 0) {
      const hasDocIdOnly = list.some((item) => item.docId && !item.docName);
      if (hasDocIdOnly) return '';
      return withKnowledgeMentionTrigger(kbName);
    }
    if (docs.length <= 2) {
      return withKnowledgeMentionTrigger(
        `${kbName}-${docs.map((d) => d.docName).join('、')}`,
      );
    }
    return withKnowledgeMentionTrigger(`${kbName} ${docs.length}${docUnit}`);
  }).filter(Boolean);

  return withTrailingSpace(parts.join(' '));
}

/**
 * 打点用：知识库引用平铺文案（≥3 篇也展开为 `@库名-文档1、文档2、…`，与提交 model query 知识库段一致）
 */
export function buildKnowledgeLogText(
  items: ISelectedKnowledgeTagData[],
  docUnit = '篇',
): string {
  if (!items?.length) return '';
  const collapsed = buildKnowledgeInlineLabel(items, docUnit).trim();
  return expandKnowledgeQueryForSubmit(collapsed, items, docUnit);
}

/**
 * 提交给模型的 query：将输入态折叠串 `@ 库名 N篇` 按选中文档平铺为
 * `@ 库名-文档1、文档2、…`（≤2 本身已是平铺，无需改）。
 */
export function expandKnowledgeQueryForSubmit(
  displayQuery: string,
  selectedKnowledge?: ISelectedKnowledgeTagData[],
  docUnit = '篇',
): string {
  if (!displayQuery || !selectedKnowledge?.length) return displayQuery;

  const byKb = new Map<string, ISelectedKnowledgeTagData[]>();
  selectedKnowledge.forEach((item) => {
    const list = byKb.get(item.kbId) || [];
    list.push(item);
    byKb.set(item.kbId, list);
  });

  let result = displayQuery;
  byKb.forEach((list) => {
    const kbName = list[0]?.kbName || '';
    if (!kbName) return;
    const docs = list.filter((item) => item.docName);
    if (docs.length < 3) return;

    const docNames = docs.map((d) => d.docName).join('、');
    const collapsedBodies = [`${kbName} ${docs.length}${docUnit}`];
    const expandedBody = `${kbName}-${docNames}`;
    const gapVariants = [
      MENTION_SERIALIZE_GAP,
      MENTION_TRIGGER_GAP,
      MENTION_KNOWLEDGE_TRIGGER_GAP,
    ];
    gapVariants.forEach((gap) => {
      const collapsed = `@${gap}${collapsedBodies[0]}`;
      const expanded = `@${gap}${expandedBody}`;
      if (result.includes(collapsed)) {
        result = result.split(collapsed).join(expanded);
      }
    });
  });
  return result;
}

/** 知识库折叠→平铺替换对，编辑时直接 replace */
export interface IKbExpandToken {
  from: string;
  to: string;
}

/** 从同轮 display / model 的公共后缀推断替换对（提交、拉历史共用） */
export function inferKbExpandToken(
  displayQuery: string,
  modelQuery: string,
): IKbExpandToken | null {
  if (!displayQuery?.trim() || !modelQuery?.trim()) return null;
  if (displayQuery === modelQuery) return null;

  let suffixLen = 0;
  const minLen = Math.min(displayQuery.length, modelQuery.length);
  while (
    suffixLen < minLen
    && displayQuery[displayQuery.length - 1 - suffixLen]
      === modelQuery[modelQuery.length - 1 - suffixLen]
  ) {
    suffixLen += 1;
  }
  if (suffixLen === 0) return null;

  const from = displayQuery.slice(0, displayQuery.length - suffixLen);
  const to = modelQuery.slice(0, modelQuery.length - suffixLen);
  if (!from.includes('@') || !to.includes('@') || from === to) return null;

  return { from, to };
}

/** 提交 / 编辑：优先用已存 token，否则从 display + model 推断 */
export function resolveKbExpandToken(
  displayQuery: string,
  modelQuery?: string | null,
  existingToken?: IKbExpandToken | null,
): IKbExpandToken | null {
  if (existingToken?.from && existingToken?.to) return existingToken;
  if (!displayQuery?.trim() || !modelQuery?.trim()) return null;
  return inferKbExpandToken(displayQuery.trim(), modelQuery.trim());
}

/**
 * 历史气泡展示串：优先 display_query；缺失时用 kb_expand_token 将 model query 折叠回展示态
 */
export function resolveHistoryDisplayQuery(options: {
  modelQuery?: string;
  displayQuery?: string;
  kbExpandToken?: IKbExpandToken | null;
}): string {
  const trimmedDisplay = options.displayQuery?.trim();
  if (trimmedDisplay) return trimmedDisplay;

  const modelQuery = options.modelQuery?.trim() || '';
  const { from, to } = options.kbExpandToken || {};
  if (from && to && modelQuery.includes(to)) {
    return modelQuery.split(to).join(from);
  }

  return modelQuery;
}

/**
 * 提交给模型的 query：有 selectedKnowledge 时展开；否则用历史 kb_expand_token 做替换。
 */
export function resolveModelQueryForSubmit(
  displayQuery: string,
  selectedKnowledge?: ISelectedKnowledgeTagData[],
  docUnit = '篇',
  kbExpandToken?: IKbExpandToken | null,
): string {
  const expanded = expandKnowledgeQueryForSubmit(
    displayQuery,
    selectedKnowledge,
    docUnit,
  );
  if (expanded !== displayQuery) return expanded;

  const { from, to } = kbExpandToken || {};
  if (from && to && displayQuery.includes(from)) {
    return displayQuery.split(from).join(to);
  }
  return displayQuery;
}

/** Chart 光标处插入文案：`# 展示名`（多个空格分隔） */
export function buildChartInlineLabel(
  charts: ISelectedChartTagData[],
  isEN: boolean,
): string {
  if (!charts?.length) return '';
  const parts = charts
    .map((chart) => {
      const name = resolveChartDisplayName(chart, isEN);
      return name ? withMentionTrigger('#', name) : '';
    })
    .filter(Boolean);
  return withTrailingSpace(parts.join(' '));
}

/**
 * 历史提问气泡展示文案：优先 display_query；旧 Skill 数据将 queryTabs 合成 `/名` 前缀
 */
export function resolveQuestionDisplayQuery(options: {
  question: string;
  displayQuery?: string;
  queryTabs?: ISelectedSkillTagData[];
  isSkillInherited?: boolean;
  isEN: boolean;
  kbExpandToken?: IKbExpandToken | null;
}): string {
  const collapsed = resolveHistoryDisplayQuery({
    modelQuery: options.question,
    displayQuery: options.displayQuery,
    kbExpandToken: options.kbExpandToken,
  });

  if (options.displayQuery?.trim()) return collapsed;

  const { from, to } = options.kbExpandToken || {};
  if (from && to && options.question?.trim().includes(to)) return collapsed;

  const { question, queryTabs, isSkillInherited, isEN } = options;
  if (isSkillInherited || !queryTabs?.length) return question;

  const prefixes = queryTabs
    .map((skill) => buildSerializedSkillInlineLabel(skill, isEN))
    .filter(Boolean);
  if (!prefixes.length) return question;

  const combinedPrefix = prefixes.join('');
  const alreadyInline = prefixes.some((prefix) =>
    mentionLabelMatchVariants(
      prefix.replace(MENTION_BOUNDARY_MARKER, ''),
    ).some((variant) => question.includes(variant)),
  );
  if (alreadyInline) return question;

  return question ? `${combinedPrefix} ${question}` : combinedPrefix;
}
